import { isSchemaNode, SchemaNode } from './types';

const normalizeNode = (node: SchemaNode): Record<string, unknown> => {
  const { children, ...rest } = node;
  return rest;
};

const areNodesEqual = (a: SchemaNode, b: SchemaNode): boolean =>
  JSON.stringify(normalizeNode(a)) === JSON.stringify(normalizeNode(b));

const getChangedKeys = (a: SchemaNode, b: SchemaNode): Set<string> => {
  const result = new Set<string>();
  const aKeys = Object.keys(normalizeNode(a));
  const bKeys = Object.keys(normalizeNode(b));
  const allKeys = new Set([...aKeys, ...bKeys]);

  allKeys.forEach(key => {
    const aVal = JSON.stringify(a[key]);
    const bVal = JSON.stringify(b[key]);
    if (aVal !== bVal) {
      result.add(key);
    }
  });

  return result;
};

const collectAllIds = (items: unknown[], result: Set<string>): void => {
  items.forEach(item => {
    if (!isSchemaNode(item)) return;

    if (item.id) {
      result.add(item.id);
    }

    if (Array.isArray(item.children)) {
      collectAllIds(item.children, result);
    } else if (isSchemaNode(item.children)) {
      collectAllIds([item.children], result);
    }
  });
};

const buildNodeMap = (
  items: unknown[],
  result: Map<string, SchemaNode>
): void => {
  items.forEach(item => {
    if (!isSchemaNode(item) || !item.id) return;

    result.set(item.id, item);

    if (Array.isArray(item.children)) {
      buildNodeMap(item.children, result);
    } else if (isSchemaNode(item.children)) {
      buildNodeMap([item.children], result);
    }
  });
};

export const getModifiedFieldIds = (
  original: unknown,
  modified: unknown
): Set<string> => {
  const result = new Set<string>();

  if (!Array.isArray(original) || !Array.isArray(modified)) {
    return result;
  }

  const originalMap = new Map<string, SchemaNode>();
  const modifiedMap = new Map<string, SchemaNode>();

  buildNodeMap(original, originalMap);
  buildNodeMap(modified, modifiedMap);

  const allOriginalIds = new Set<string>();
  const allModifiedIds = new Set<string>();
  collectAllIds(original, allOriginalIds);
  collectAllIds(modified, allModifiedIds);

  allModifiedIds.forEach(id => {
    if (!allOriginalIds.has(id)) {
      result.add(id);
    }
  });

  allOriginalIds.forEach(id => {
    if (!allModifiedIds.has(id)) {
      result.add(id);
    }
  });

  originalMap.forEach((originalNode, id) => {
    const modifiedNode = modifiedMap.get(id);
    if (modifiedNode && !areNodesEqual(originalNode, modifiedNode)) {
      result.add(id);
    }
  });

  return result;
};

export const hasConflictingChanges = (
  localModifiedIds: Set<string>,
  remoteModifiedIds: Set<string>
): boolean => {
  // This is just !isDisjointFrom (low browser support)
  // !localModifiedIds.isDisjointFrom(remoteModifiedIds)
  let hasConflict = false;
  localModifiedIds.forEach(id => {
    if (remoteModifiedIds.has(id)) {
      hasConflict = true;
    }
  });
  return hasConflict;
};

export const getConflictingIds = (
  localModifiedIds: Set<string>,
  remoteModifiedIds: Set<string>
): Set<string> => {
  // This is just intersection (low browser support)
  // localModifiedIds.intersection(remoteModifiedIds)
  const result = new Set<string>();
  localModifiedIds.forEach(id => {
    if (remoteModifiedIds.has(id)) {
      result.add(id);
    }
  });
  return result;
};

export const getModifiedFieldKeys = (
  original: unknown,
  modified: unknown
): Map<string, Set<string>> => {
  const result = new Map<string, Set<string>>();

  if (!Array.isArray(original) || !Array.isArray(modified)) {
    return result;
  }

  const originalMap = new Map<string, SchemaNode>();
  const modifiedMap = new Map<string, SchemaNode>();

  buildNodeMap(original, originalMap);
  buildNodeMap(modified, modifiedMap);

  const allOriginalIds = new Set<string>();
  const allModifiedIds = new Set<string>();
  collectAllIds(original, allOriginalIds);
  collectAllIds(modified, allModifiedIds);

  allModifiedIds.forEach(id => {
    if (!allOriginalIds.has(id)) {
      result.set(id, new Set(['*']));
    }
  });

  allOriginalIds.forEach(id => {
    if (!allModifiedIds.has(id)) {
      result.set(id, new Set(['*']));
    }
  });

  originalMap.forEach((originalNode, id) => {
    const modifiedNode = modifiedMap.get(id);
    if (modifiedNode && !areNodesEqual(originalNode, modifiedNode)) {
      const changedKeys = getChangedKeys(originalNode, modifiedNode);
      result.set(id, changedKeys);
    }
  });

  return result;
};
